import { useCallback, useEffect, useRef, useState } from 'react';
import { sameDraft } from './MyPage.shared';

/**
 * One editor card's working copy (design §8.2 — every save is a partial update).
 *
 *   - `source` is what the server last said. While the doctor has not typed,
 *     a new source (a reload, another card's save) replaces the draft; once
 *     they have, their keystrokes are never overwritten from under them.
 *   - `adopt(value)` after a successful save makes the SAVED value the new
 *     baseline — which may differ from what was typed (the service sanitises
 *     markdown, reduces a video URL to an id), so "dirty" means "differs from
 *     what is stored", never "differs from what I sent".
 *   - `reset()` throws the draft away.
 */
export function useDraft(source) {
  const key = JSON.stringify(source);
  const baseRef = useRef(source);
  const [draft, setDraft] = useState(source);

  useEffect(() => {
    const prev = baseRef.current;
    baseRef.current = source;
    setDraft((d) => (sameDraft(d, prev) ? source : d));
    // `key` stands for `source` by value.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const adopt = useCallback((value) => {
    baseRef.current = value;
    setDraft(value);
  }, []);

  const reset = useCallback(() => setDraft(baseRef.current), []);

  return { draft, setDraft, dirty: !sameDraft(draft, baseRef.current), adopt, reset };
}

export default useDraft;
